import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  styled,
  Tooltip,
  IconButton,
  CircularProgress,
  TextField,
  Select,
  FormControl,
  InputLabel,
  MenuItem as MuiMenuItem,
  Pagination,
} from "@mui/material";
import {
  Add,
  DeleteOutline,
  DownloadOutlined,
  EditOutlined,
  Search,
} from "@mui/icons-material";
import CommonHeader from "../../CommonComponent/CommonHeader";
import ButtonCommon from "../../CommonComponent/ButtonCommon";
import { Eye } from "../../assets/svg";
import { deletePrivacyPolicy, getPrivacyPolicy } from "../../api/privacyPolicy";
import { showErrorToast, showSuccessToast } from "../../toastUtils";
import { useNavigate } from "react-router-dom";
import ISO6391 from "iso-639-1";
import ConfirmationModal from "../../CommonComponent/CommonConfirmationModal";

const PrivacyPolicy = () => {
  const [privacyPolicy, setPrivacyPolicy] = useState([]);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const rowsPerPage = 5; // Fixed rows per page
  const navigate = useNavigate();

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("All");
  const [selectedFramework, setSelectedFramework] = useState("All");

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-head": {
      backgroundColor: "#F9FAFB",
      color: "#000000",
      fontWeight: 600,
    },
    "&.MuiTableCell-body": {
      fontSize: 14,
    },
  }));

  useEffect(() => {
    const fetchPolicy = async () => {
      setLoading(true);
      try {
        const data = await getPrivacyPolicy();
        if (data?.status_code === 200) {
          setPrivacyPolicy(data?.data || []);
          setFilteredPolicies(data?.data || []);
        } else {
          setPrivacyPolicy([]);
          setFilteredPolicies([]);
          setLoading(false);
          // showErrorToast(data.message);
        }
      } catch (err) {
        showErrorToast("Failed to fetch privacy policy");
      } finally {
        setLoading(false);
      }
    };
    fetchPolicy();
  }, []);

  // Filtering logic
  useEffect(() => {
    let filtered = privacyPolicy;

    if (searchQuery) {
      filtered = filtered.filter((policy) =>
        policy.policy_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedLanguage !== "All") {
      filtered = filtered.filter(
        (policy) => policy.language === selectedLanguage
      );
    }

    if (selectedFramework !== "All") {
      filtered = filtered.filter(
        (policy) => policy.legal_framework === selectedFramework
      );
    }

    setFilteredPolicies(filtered);
    setPage(1); // Reset to first page after filtering
  }, [searchQuery, selectedLanguage, selectedFramework, privacyPolicy]);

  // Handle pagination change
  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleViewClick = (row) => {
    navigate(`/privacy-policy/${row.policy_id}`);
  };

  const handleOpenModal = (policyId) => {
    setPolicyToDelete(policyId);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setPolicyToDelete(null);
  };

  const handleDelete = async (privacyId) => {
    setLoading(true);
    try {
      const result = await deletePrivacyPolicy(privacyId);
      if (result?.status_code === 200) {
        const data = await getPrivacyPolicy();
        setPrivacyPolicy(data?.data || []);
        showSuccessToast("Deleted Successfully");
        setLoading(false);
      }
    } catch (error) {
      showErrorToast(error);
      setLoading(false);
      console.error("Failed to delete policy", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadHtml = (htmlContent, policyName) => {
    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    // Sanitize filename (optional but recommended)
    const sanitizedFileName = policyName
      ? policyName.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9-_]/g, "")
      : "privacy_policy";

    a.href = url;
    a.download = `${sanitizedFileName}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleEdit = (privacyId) => {
    navigate("/privacy-settings", {
      state: {
        privacyId,
        isEditing: true,
      },
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", mb: 3 }}>
      {/* Header & Button */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <CommonHeader
          title="Privacy Policy"
          subTitle="Get your detailed privacy policy"
        />
        <ButtonCommon
          icon={<Add />}
          onClick={() => navigate("/privacy-settings")}
        >
          Generate
        </ButtonCommon>
      </Box>

      {/* Search and Filters */}
      <Box display="flex" gap={2} mb={2}>
        <TextField
          label="Search by Policy Name"
          variant="outlined"
          size="small"
          fullWidth
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{ endAdornment: <Search /> }}
        />

        <FormControl size="small" fullWidth>
          <InputLabel>Filter by Language</InputLabel>
          <Select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            label="Filter by Language"
          >
            <MuiMenuItem value="All">All</MuiMenuItem>
            {[
              ...new Set(
                (Array.isArray(privacyPolicy) ? privacyPolicy : []).map(
                  (policy) => policy.language
                )
              ),
            ].map((lang) => (
              <MuiMenuItem key={lang} value={lang}>
                {ISO6391.getName(lang)}
              </MuiMenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel>Filter by Framework</InputLabel>
          <Select
            value={selectedFramework}
            onChange={(e) => setSelectedFramework(e.target.value)}
            label="Filter by Framework"
          >
            <MuiMenuItem value="All">All</MuiMenuItem>
            {[
              ...new Set(
                (Array.isArray(privacyPolicy) ? privacyPolicy : []).map(
                  (policy) => policy.legal_framework
                )
              ),
            ].map((fw) => (
              <MuiMenuItem key={fw} value={fw}>
                {fw}
              </MuiMenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <ConfirmationModal
        open={openModal}
        onClose={handleCloseModal}
        title="Delete Policy"
        description="Are you sure you want to delete this privacy policy?"
        onConfirm={() => {
          handleDelete(policyToDelete);
          handleCloseModal();
        }}
        loading={loading}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
      {/* Table */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2 }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Policy ID</StyledTableCell>
              <StyledTableCell>Policy Name</StyledTableCell>
              <StyledTableCell>Framework</StyledTableCell>
              <StyledTableCell>Language</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (filteredPolicies || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No Privacy Policy found.
                </TableCell>
              </TableRow>
            ) : (
              (Array.isArray(filteredPolicies) ? filteredPolicies : [])
                .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                .map((policy) => (
                  <TableRow key={policy.policy_id}>
                    <StyledTableCell>{policy.policy_id}</StyledTableCell>
                    <Tooltip title={policy.policy_name} arrow>
                      <StyledTableCell>
                        {policy.policy_name.length > 20
                          ? `${policy.policy_name.slice(0, 20)}...`
                          : policy.policy_name}
                      </StyledTableCell>
                    </Tooltip>
                    <StyledTableCell>{policy.legal_framework}</StyledTableCell>
                    <StyledTableCell>
                      {ISO6391.getName(policy.language)}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Tooltip title="View" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleViewClick(policy)}
                        >
                          <Eye />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(policy.policy_id)}
                        >
                          <EditOutlined />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(policy.policy_id)}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </Tooltip>
                      {/* {policy.hosting_preference !== "dpc" && ( */}
                      <Tooltip title="Download" arrow>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleDownloadHtml(
                              policy.html_content,
                              policy.policy_name
                            )
                          }
                        >
                          <DownloadOutlined />
                        </IconButton>
                      </Tooltip>
                      {/* )} */}
                    </StyledTableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {filteredPolicies.length > rowsPerPage && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={Math.ceil(filteredPolicies.length / rowsPerPage)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
            color="primary"
            size="large"
          />
        </Box>
      )}
    </Box>
  );
};

export default PrivacyPolicy;
